// Utility functions to convert between HTML and EditorJS format

export const convertHtmlToEditorJS = (html) => {
  if (!html) {
    return { blocks: [] };
  }
  
  // If it's already EditorJS format, return it
  if (typeof html === 'object' && html.blocks && Array.isArray(html.blocks)) {
    return html;
  }

  // If it's a string, try to parse it
  if (typeof html !== 'string') {
    return { blocks: [] };
  }

  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html.trim();

  const blocks = [];

  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) {
        blocks.push({
          type: 'paragraph',
          data: {
            text: text
          }
        });
      }
      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      const text = node.textContent.trim();

      switch(tagName) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          const level = parseInt(tagName.charAt(1));
          if (text) {
            blocks.push({
              type: 'header',
              data: {
                text: text,
                level: level
              }
            });
          }
          break;
        case 'p':
          if (text) {
            blocks.push({
              type: 'paragraph',
              data: {
                text: text
              }
            });
          }
          break;
        case 'ul':
        case 'ol':
          const items = Array.from(node.querySelectorAll('li')).map(li => li.textContent.trim()).filter(item => item);
          if (items.length > 0) {
            blocks.push({
              type: 'list',
              data: {
                style: tagName === 'ol' ? 'ordered' : 'unordered',
                items: items
              }
            });
          }
          break;
        case 'blockquote':
          if (text) {
            blocks.push({
              type: 'quote',
              data: {
                text: text,
                caption: ''
              }
            });
          }
          break;
        case 'img':
          const src = node.getAttribute('src');
          if (src) {
            blocks.push({
              type: 'image',
              data: {
                file: {
                  url: src
                },
                caption: node.getAttribute('alt') || '',
                withBorder: false,
                withBackground: false,
                stretched: false
              }
            });
          }
          break;
        default:
          // For other elements, just extract text
          if (text && !node.querySelector('h1, h2, h3, h4, h5, h6, p, ul, ol, blockquote')) {
            blocks.push({
              type: 'paragraph',
              data: {
                text: text
              }
            });
          }
      }

      // Process child nodes
      Array.from(node.childNodes).forEach(child => {
        if (child.nodeType === Node.ELEMENT_NODE && !['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol', 'blockquote', 'li'].includes(child.tagName.toLowerCase())) {
          processNode(child);
        }
      });
    }
  };

  // Process all top-level nodes
  Array.from(tempDiv.childNodes).forEach(node => {
    processNode(node);
  });

  // If no blocks were created, create at least one paragraph
  if (blocks.length === 0 && html.trim()) {
    blocks.push({
      type: 'paragraph',
      data: {
        text: tempDiv.textContent.trim() || html.trim()
      }
    });
  }

  return { blocks: blocks };
};

export const convertEditorJSToHtml = (editorData) => {
  if (!editorData || !editorData.blocks || !Array.isArray(editorData.blocks)) {
    return '';
  }

  return editorData.blocks.map(block => {
    switch(block.type) {
      case 'paragraph':
        return `<p>${escapeHtml(block.data.text || '')}</p>`;
      case 'header':
        const level = block.data.level || 2;
        return `<h${level}>${escapeHtml(block.data.text || '')}</h${level}>`;
      case 'list':
        const tag = block.data.style === 'ordered' ? 'ol' : 'ul';
        const items = (block.data.items || []).map(item => `<li>${escapeHtml(item)}</li>`).join('');
        return `<${tag}>${items}</${tag}>`;
      case 'quote':
        return `<blockquote>${escapeHtml(block.data.text || '')}</blockquote>`;
      case 'image':
        const url = block.data.file?.url || block.data.url || '';
        const caption = block.data.caption || '';
        return `<img src="${url}" alt="${escapeHtml(caption)}" />`;
      case 'delimiter':
        return '<hr />';
      default:
        return '';
    }
  }).join('');
};

const escapeHtml = (text) => {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

